/**
 * Parses an axios error response into a human-readable string.
 * Handles multiple content types and data shapes returned by the Blimp API:
 *   - JSON with { details: [{ message }] } (validation errors)
 *   - JSON with { message } or { error }
 *   - Plain text
 *   - Binary (ArrayBuffer / Blob) that must be decoded first
 */
export const parseApiError = async (error: any): Promise<string> => {
  if (!error.response) return 'Network error';

  const contentType: string = error.response.headers?.['content-type'] ?? '';
  let errorData = error.response.data;

  try {
    // Decode binary payloads before attempting JSON/text parsing
    if (contentType.includes('application/json')) {
      if (errorData instanceof ArrayBuffer) {
        errorData = JSON.parse(new TextDecoder('utf-8').decode(errorData));
      } else if (errorData instanceof Blob) {
        errorData = JSON.parse(await errorData.text());
      } else if (typeof errorData === 'string') {
        errorData = JSON.parse(errorData);
      }
    } else if (contentType.includes('text/')) {
      if (errorData instanceof ArrayBuffer) {
        errorData = new TextDecoder('utf-8').decode(errorData);
      } else if (errorData instanceof Blob) {
        errorData = await errorData.text();
      }
    }

    if (errorData && typeof errorData === 'object') {
      // Prefer the first detail entry targeting 'Message' (Blimp validation shape)
      if (Array.isArray(errorData.details) && errorData.details.length > 0) {
        const lastDetail = errorData.details[errorData.details.length - 1];
        return lastDetail?.message ?? 'Error';
      }
      if (errorData.message) return errorData.message;
      if (errorData.error) return errorData.error;
    }

    if (typeof errorData === 'string') return errorData;
  } catch {
    // Parsing failed — fall through to generic message
  }

  return 'Error';
};
